/// <reference path="../../../../../typings/jquery.d.ts"/>
import { AppUtils } from '../../../../../../shared/utils/index';
import { User } from '../../../../../../shared/models/api/User/User';
import { BasicApplicationRouter } from '../../../../../../shared/services/BasicApplicationRouter';
import { BankAccountService } from '../../../../../../shared/services/BankAccountService';
import { VesDamageValidationService } from '../../../../../../shared/services/VesDamageValidationService';
import { AppPersistenceService } from '../../services/AppPersistenceService';
import { AppViewService } from '../../services/AppViewService';
import { PersonalDataFormModel } from '../../models/PersonalDataFormModel';
import { CheckboxStateEnum } from '../../../../../../shared/enums/CheckboxSelectionType';
import { AppApplicationModel } from '../../models/AppApplicationModel';

export class PersonalDataOhcPageController implements StepController {
	private readonly $ctx: JQuery;
	private readonly applicationRouter: BasicApplicationRouter<DamageDeclarationOhcAppDefinition>;
	private readonly user: User;
	private readonly options: DamageDeclarationOhcOptions;
	private readonly appViewService: AppViewService;
	private readonly appPersistenceService: AppPersistenceService;
	private readonly parent: AppController;
	private readonly loader: BasicLoader<DamageDeclarationOhcAppDefinition>;
	private appValidationService: VesDamageValidationService;
	private readonly bankAccountService: BankAccountService;
	private readonly iban: string;
	private readonly bic: string;
	private readonly loaderIdIbanChange = "PersonalDataOhcPageController ibanChangeListener";

	constructor(parent: AppController, options: DamageDeclarationOhcOptions, loader: BasicLoader<DamageDeclarationOhcAppDefinition>, user: User) {
		this.parent = parent;
		this.options = options;
		this.loader = loader;
		this.user = user;
		this.$ctx = $(options.$ctx);
		this.iban = 'Iban';
		this.bic = 'Bic';

		this.applicationRouter = new BasicApplicationRouter(options);
		this.appPersistenceService = new AppPersistenceService(options);
		this.appViewService = new AppViewService(parent, options, loader);
		this.bankAccountService = new BankAccountService(options, user);
	}

	public start(): void {
		if (this.checkPreconditions()) {
			const viewConfig = {
				data: this.getViewOptions(),
				templateurl: this.options.app.currenttemplate.templateurl,
				campaignurls: this.options.app.campaignurls,
				parent: this,
				$ctx: this.$ctx,
				options: this.options,
				callback: this.afterRenderApp
			};
			this.appViewService.render(viewConfig);
		} else {
			T.Utils.Helper.routeToUrl(this.options.app.entryredirecturl);
		}
	}

	private checkPreconditions(): boolean {
		const documentsBillsModel = this.appPersistenceService.getDocumentsBillsOhcFormModel();
		return $('body').hasClass(this.options.app.pageEditorClass) || (documentsBillsModel !== undefined);
	}

	/**
	 * returns view options for the page.
	 * only variables defined in here are available in the view to use with handlebars.
	 */
	private getViewOptions(): { [key: string]: any } {
		const appModel = this.getMergedFormModel();
		return {
			User: this.user,
			PersonalDataModel: appModel.PersonalDataFormModel,
			CostLimit: appModel.CostLimit,
			DamageType: appModel.DamageFormModel.SchadenTyp,
			nexturl: this.options.app.nexturl || '',
			prevurl: this.options.app.prevurl || '',
		};
	}

	private getMergedFormModel(): AppApplicationModel {
		const formModel = this.appPersistenceService.getApplicationModel();
		if (this.user && this.user.Valid) {
			formModel.mergeWithUser(this.user, false);
		}
		this.appPersistenceService.setPersonalDataOhcFormModel(formModel.PersonalDataFormModel);
		return formModel;
	}

	public afterRenderApp(): void {
		this.loader.loadSubApps({ options: this.options }, () => {
			this.appValidationService = new VesDamageValidationService({ options: this.options }, this.appViewService.getForm(), true);
			this.parent.afterRenderApp();
			const formModel = this.appPersistenceService.getPersonalDataOhcFormModel();
			this.markUntouchedSelectionsAsUndefined(formModel);

			this.appViewService.addPersonalDataListeners(
				this.ibanChangeListener.bind(this),
				this.abweichenderKontoinhaberChangeListener.bind(this),
				this.viewChangeListener.bind(this),
				this.submitListener.bind(this),
				this.checkCountryCodeIban.bind(this),
				this.changeBic.bind(this)
			);

			this.appViewService.toggleAndFillKontoinhaber();
			this.appViewService.restoreSectionBackground();

			if (formModel) {
				if (formModel.Iban) {
					this.ibanChangeListener();
				}
				if (formModel.Bic) {
					this.changeBic();
				}
			}
			AppUtils.View.stopLoader("damagedeclarationohc initApplication");
		});
	}

	public checkCountryCodeIban(): void {
		this.appViewService.checkCountryCodeIban();
	}

	/**
	 * Set properties manual as undefined when user has not selected an option for property
	 */
	private markUntouchedSelectionsAsUndefined(personalDataModel: PersonalDataFormModel): void {
		if (personalDataModel) {
			if (personalDataModel.AbweichenderKontoInhaber === undefined) {
				personalDataModel.VersicherungUnfallschutz = CheckboxStateEnum.CHECKBOX_UNTOUCHED;
			}
			if (personalDataModel.VersicherungReiseschtuz === undefined) {
				personalDataModel.VersicherungReiseschtuz = CheckboxStateEnum.CHECKBOX_UNTOUCHED;
			}
			if (personalDataModel.VersicherungUnfallschutz === undefined) {
				personalDataModel.VersicherungUnfallschutz = CheckboxStateEnum.CHECKBOX_UNTOUCHED;
			}
		}
	}

	private abweichenderKontoinhaberChangeListener(): void {
		this.appViewService.toggleAndFillKontoinhaber();
	}

	private ibanChangeListener(): void {
		const formModel = this.appViewService.getFormModelFromPersonalDataView();
		AppUtils.View.startLoader(this.loaderIdIbanChange);
		this.bankAccountService.checkIban(formModel.Iban.replace(/ /g, '')
			, '', this.handleCheckIbanSuccess.bind(this), this.handleCheckIbanError.bind(this));
	}

	private handleCheckIbanSuccess(): void {
		this.appValidationService.setServerValidationFlag(true, this.iban);
		this.appValidationService.triggerValidation([this.iban]);
		AppUtils.View.stopLoader(this.loaderIdIbanChange);
	}
	private handleCheckIbanError(): void {
		this.appValidationService.setServerValidationFlag(false, this.iban);
		this.appValidationService.triggerValidation([this.iban]);
		AppUtils.View.stopLoader(this.loaderIdIbanChange);
		AppUtils.View.scrollTo(this.$ctx.find(`[name="${this.iban}"]`).offset().top - 100, 1000);
	}

	public changeBic(): void {
		const formModel = this.appViewService.getFormModelFromPersonalDataView();
		this.bankAccountService.checkIbanWithBic(formModel.Iban, formModel.Bic, this.checkBicSuccess.bind(this), this.checkBicError.bind(this));
	}
	private checkBicSuccess(): void {
		this.appValidationService.setServerValidationFlag(true, this.bic);
		this.appValidationService.triggerValidation([this.bic]);
	}
	private checkBicError(jqXHR: JQueryXHR): void {
		this.appValidationService.setServerValidationFlag(false, this.bic);
		this.appValidationService.triggerValidation([this.bic]);
		AppUtils.View.scrollTo(this.$ctx.find(`[name="${this.bic}"]`).offset().top - 100, 1000);
		if (jqXHR.status === 404) {
			jqXHR.errorHandled = true;
		}
	}

	private viewChangeListener(): void {
		AppUtils.Helper.scheduleTask(() => {
			const formModel: PersonalDataFormModel = this.appViewService.getFormModelFromPersonalDataView();
			this.appPersistenceService.setPersonalDataOhcFormModel(formModel);
			this.appViewService.restoreSectionBackground();
		}, 'user-visible');
	}

	private submitListener(event: JQuery.Event): void {
		event.preventDefault();

		if (this.appValidationService.isValid()) {
			AppUtils.View.startLoader("PersonalDataOhcPageController submitListener");
			this.viewChangeListener();
			this.applicationRouter.next();
			AppUtils.View.stopLoader("PersonalDataOhcPageController submitListener");
		}
	}
}
